import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import {
  Pencil, FileSignature, GraduationCap, BookOpenCheck, BookOpen,
  Lightbulb, ScanText, RefreshCw, TrendingUp, Lock, Crown
} from "lucide-react";

const ICONS = { Pencil, FileSignature, GraduationCap, BookOpenCheck, BookOpen, Lightbulb, ScanText, RefreshCw };
const BG = {
  amber: "bg-amber-100 text-amber-700",
  indigo: "bg-indigo-100 text-indigo-700",
  rose: "bg-rose-100 text-rose-700",
  violet: "bg-violet-100 text-violet-700",
  emerald: "bg-emerald-100 text-emerald-700",
  sky: "bg-sky-100 text-sky-700",
  orange: "bg-orange-100 text-orange-700",
};
const BAR = {
  amber: "bg-amber-500",
  indigo: "bg-indigo-500",
  rose: "bg-rose-500",
  violet: "bg-violet-500",
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  orange: "bg-orange-500",
};
const TIER_PILL = {
  bronze: "bg-amber-700 text-amber-50",
  silver: "bg-slate-400 text-white",
  gold: "bg-amber-400 text-slate-900",
  diamond: "bg-gradient-to-r from-cyan-400 to-violet-400 text-white",
};

export default function GrowthPaths() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/growth-paths").then(r => setData(r.data)); }, []);

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-indigo-100 text-indigo-700 grid place-items-center"><TrendingUp size={20}/></div>
            <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Growth Paths</h1>
            {data.is_premium && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <Crown size={12}/> Premium
              </span>
            )}
          </div>
          <p className="text-slate-600 max-w-2xl">Nine mastery paths — each tracks your sessions, unlocks tiers, and shows what to practice next. Bronze → Silver → Gold → <span className="font-bold">Diamond</span> (Premium).</p>
        </div>
        {!data.is_premium && (
          <Link to="/app/upgrade" data-testid="paths-upgrade-link"
            className="rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-4 py-2 text-sm inline-flex items-center gap-2 tactile-shadow-amber">
            <Crown size={14}/> Unlock Diamond
          </Link>
        )}
      </div>

      {/* Tier legend */}
      <div className="flex flex-wrap gap-2">
        {data.tiers.map(t => (
          <span key={t.id} data-testid={`tier-legend-${t.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${TIER_PILL[t.id] || 'bg-slate-200 text-slate-700'} ${t.premium && !data.is_premium ? 'opacity-60' : ''}`}>
            {t.premium && !data.is_premium && <Lock size={11}/>} {t.name} · {t.threshold}+ sessions
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.paths.map((p) => {
          const Icon = ICONS[p.icon] || Pencil;
          return (
            <div key={p.id} data-testid={`path-${p.id}`} className="tactile-card p-6">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-12 h-12 rounded-2xl grid place-items-center ${BG[p.color] || BG.indigo}`}>
                  <Icon size={22}/>
                </div>
                {p.current_tier && (
                  <span data-testid={`path-tier-${p.id}`} className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${TIER_PILL[p.current_tier]}`}>
                    {p.current_tier_name}
                  </span>
                )}
              </div>
              <h3 className="text-xl font-bold mb-1" style={{fontFamily:'Outfit'}}>{p.name}</h3>
              <p className="text-sm text-slate-600 mb-4">{p.description}</p>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-widest">
                  <span data-testid={`path-count-${p.id}`}>{p.sessions_total} sessions</span>
                  <span>{p.next_tier_name ? `Next: ${p.next_tier_name} (${p.next_tier_threshold})` : "Max tier reached"}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div data-testid={`path-bar-${p.id}`} className={`h-full ${BAR[p.color] || BAR.indigo} transition-all`}
                    style={{width: `${p.progress_pct}%`}} />
                </div>
              </div>

              {p.diamond_locked && (
                <div className="mt-4 text-xs text-slate-500 flex items-center gap-1.5">
                  <Lock size={12}/> Diamond tier (50+ sessions) requires Premium
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
